import { Dispatch, SetStateAction } from 'react';

import { createComingSoonEvent } from '@/common/analytics/gtm/Homepage/comingSoonAnalytics';
import { createNotifyEventName } from '@/common/analytics/gtm/shared/trackNotify/trackNotifyAnalytics';
import { trackGA4SelectPromotion } from '@/common/analytics/gtm/shared/trackPromotionClick';
import Badge from '@/common/components/Badge/Badge';
import EventTileWithImpression from '@/common/components/EventTile/EventTileWithImpression';
import NotifyButton from '@/common/components/NotifyButton/NotifyButton';
import SliderWithImpressions from '@/common/components/Slider/SliderWithImpressions';
import UpcomingCounter from '@/common/components/UpcomingCounter/UpcomingCounter';
import useModalContext from '@/common/contexts/ModalContext/useModalContext';
import ComingSoonHeader from '@/modules/HomePage/components/ComingSoon/ComingSoonHeader/ComingSoonHeader';
import { HomepageUpcomingEventV2 } from 'generatedBfw/static';

import styles from './comingSoon.module.scss';
import ComingSoonPopup from './ComingSoonPopup/ComingSoonPopup';

interface Props {
  events: HomepageUpcomingEventV2[];
  setExpiredEvents: Dispatch<SetStateAction<string[]>>;
}

const ComingSoonView = ({ events, setExpiredEvents }: Props) => {
  const { displayModal, closeModal } = useModalContext();

  return (
    <section className={styles.container} data-testid="coming-soon">
      <ComingSoonHeader />
      <SliderWithImpressions
        keyExtractor={(event) => event.id}
        metadata={{ elementVariant: 'coming_soon' }}
        renderSlide={(event, index, useSlideImpression) => {
          const metadata = createComingSoonEvent(event, index + 1);

          return (
            <EventTileWithImpression
              author={event.author}
              badge={event.badge ? <Badge type={event.badge} /> : undefined}
              className={styles.event}
              files={event.files}
              imageBadge={
                event.startsAt ? (
                  <UpcomingCounter
                    date={event.startsAt}
                    onEnd={() =>
                      setExpiredEvents((prevExpiredEvents) => [...prevExpiredEvents, event.id])
                    }
                  />
                ) : undefined
              }
              metadata={metadata}
              onClick={() => {
                displayModal(
                  <ComingSoonPopup
                    event={event}
                    onClose={closeModal}
                    slotBottom={
                      <NotifyButton
                        className={styles.notifyButtonOnPopup}
                        eventId={event.id}
                        isFullWidth
                        metadata={{
                          componentName: 'ComingSoon',
                          name: createNotifyEventName(event.title, event.author),
                          id: event.id,
                        }}
                        onClick={() => {
                          trackGA4SelectPromotion(metadata);
                        }}
                      />
                    }
                  />,
                );
              }}
              slotRight={
                <NotifyButton
                  className={styles.eventNotify}
                  eventId={event.id}
                  metadata={{
                    componentName: 'ComingSoon',
                    name: createNotifyEventName(event.title, event.author),
                    id: event.id,
                  }}
                  onClick={() => {
                    trackGA4SelectPromotion(metadata);
                  }}
                />
              }
              title={event.title}
              useSlideImpression={useSlideImpression}
              vertical
            />
          );
        }}
        slides={events}
      />
    </section>
  );
};

export default ComingSoonView;
